/** Browser-local projection of the shared TSS UI observation envelope. */

import type {
  UIEventOrigin,
  UIEventPhase,
} from '@trafilea/tss-lib-funnel-schema-ts';

export interface TssTenantScope {
  tenantId?: string;
  storeId?: string;
}

export interface TssEventMetadata {
  eventId: string;
  occurredAt: string;
  source: string;
  /**
   * Semantic execution phase. Intents never cross the browser transport
   * boundary; results are emitted only after the owning handler succeeds.
   */
  phase: UIEventPhase;
  /** Runtime layer that created this envelope. */
  origin: UIEventOrigin;
  correlationId?: string;
  sessionId?: string;
  scope?: TssTenantScope;
  /**
   * Loop-guard hop count (WP-2 H5). 0/undefined for a fresh emission; each
   * dispatch_event re-emission increments it so the reactions dispatcher can
   * drop runaway listener→dispatch_event→listener chains.
   */
  hopCount?: number;
}

export interface TssEventEnvelope<TPayload = unknown> {
  name: string;
  metadata: TssEventMetadata;
  payload: TPayload;
}

export interface UIEventPayloadContext {
  funnelId?: string;
  stepId?: string;
  blockId?: string;
  blockType?: string;
  trigger?: string;
  pageType?: string;
  currentUrl?: string;
  variantKey?: string;
}

export interface UIEventPayload {
  params: Record<string, unknown>;
  context: UIEventPayloadContext;
}

export function createEventEnvelope<TPayload>(
  name: string,
  source: string,
  payload: TPayload,
  metadata: Partial<TssEventMetadata> = {},
): TssEventEnvelope<TPayload> {
  const id =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return {
    name,
    payload,
    metadata: {
      eventId: metadata.eventId ?? id,
      occurredAt: metadata.occurredAt ?? new Date().toISOString(),
      source,
      phase: metadata.phase ?? 'observation',
      origin: metadata.origin ?? 'block',
      correlationId: metadata.correlationId,
      sessionId: metadata.sessionId,
      scope: metadata.scope,
      hopCount: metadata.hopCount,
    },
  };
}
